import { FC, useState, useEffect, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { ServerError, useMutation } from '@apollo/client'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { fetchStop, fetchStartWithForward, fetchStart } from '../../../../redux/actions/common'
import mutations from '../../../../setup/graphql/mutations'
import Alert from '../../../../_metronic/partials/alert'
import { setLoanApplication, setLoanStep, setSingPassInfo } from '../redux/actions'
import { LeftArowIcon } from '../../../../svg/LeftArowIcon'
import { RightAeroIcon } from '../../../../svg/RightAeroIcon'
import { Xicon } from '../../../../svg/Xicon'
import { UploadIcon } from '../../../../svg/UploadIcon'
import { Stack } from 'react-bootstrap'
import { setEntityData } from '../redux/actions'
import { validateUEN } from '../../../../_metronic/helpers/validation'
import { privacyPolicy } from '../../../../_metronic/helpers'
import { cleanFileNameDynamic, formatEntityDataForRedux } from '../../../constants/commonFunctions'
import { isValidPhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js'

/**
 * Helper function to transform entity data from SingPass format to the application's expected format
 * @param entityData - The raw entity data from SingPass
 * @returns Formatted entity data for use with setEntityData
 */
/**
 * Converts flat entity data to the nested JSON structure format for visualization
 */

export const historyHead = [
  'yearofassessment',
  'trade',
  'category',
  'employment',
  'amount',
  'interest',
  'rent',
  'taxclearance',
]
export const shareholderHead = [
  'entity-reference',
  'person-reference',
  'allocation',
  'category',
  'share-type',
  'currency',
]
export const financialHead = [
  'currency',
  'company-revenue',
  'company-profit-loss-after-tax',
  'company-profit-loss-before-tax',
  'current-period-end-date',
  'current-period-start-date',
]
export const corppassHead = ['person-reference', 'corppass-email', 'corppass-mobileno']
export const capitalHead = [
  'capital-type',
  'currency',
  'issued-capital-amount',
  'paid-up-capital-amount',
  'share-allotted-amount',
]
export const appointmentHead = [
  'person-reference',
  'category',
  'position',
  'appointment-date',
  'corppass-email',
  'corppass-mobileno',
]

export const toStr = (data: any, type: any) => {
  if (data === undefined || data === null) return null

  if (type === 'ADDRESSLOCAL') {
    let val = ''
    if (data?.type === 'SG') {
      if (data?.block?.value) val = val + 'BLK ' + data.block.value
      if (data?.building?.value) val = val + ' ' + data.building.value
      if (data?.floor?.value) val = val + ' ' + data.floor.value
      if (data?.unit?.value) val = val + '-' + data.unit.value
      if (data?.street?.value) val = val + ' ' + data.street.value
      if (data?.postal?.value) val = val + ' S(' + data.postal.value + ')'
    } else if (data?.type === 'Unformatted') {
      if (data?.line1) val = data?.line1?.value
      if (data?.line2) val = val + ' ' + data?.line2?.value
    }
    return val
  } else if (type === 'PHONENUMLOCAL') {
    let val = data.prefix.value + data.areacode.value + ' ' + data.nbr.value
    return val
  } else if (type === 'MONEY') {
    let val = data.value.replace(/,/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return val
  } else if (type === 'HOUSINGTYPE') {
    let val = ''
    if (data?.housingtype?.desc) val = data.housingtype.desc
    else if (data?.hdbtype?.desc) val = data.hdbtype.desc
    return val
  } else if (type === 'PRIMARYSSIC' && data?.['basic-profile']?.['primary-activity']) {
    let val = data['basic-profile']['primary-activity'].desc
      ? data['basic-profile']['primary-activity'].code +
        ' - ' +
        data['basic-profile']['primary-activity'].desc
      : data['basic-profile']['primary-activity'].code
    return val
  } else if (type === 'SECONDARYSSIC' && data?.['basic-profile']?.['secondary-activity']) {
    let val = data['basic-profile']['secondary-activity'].desc
      ? data['basic-profile']['secondary-activity'].code +
        ' - ' +
        data['basic-profile']['secondary-activity'].desc
      : data['basic-profile']['secondary-activity'].code
    return val
  } else if (type === 'ENTITYADDRESS') {
    let val = ''
    let adddata = data['addresses']['addresses-list'] as any
    if (adddata[0] !== undefined) {
      adddata = adddata[0]
    }
    let standard = adddata?.['standard']?.code
    if (standard === 'D') {
      let add = adddata
      if (add?.block?.value) val = val + 'BLK ' + add.block.value
      // if (add.building.value !== undefined && add.building.value != "")
      if (add?.building?.value) val = val + ' ' + add.building.value
      if (add?.floor?.value) val = val + ' ' + add.floor.value
      if (add?.unit?.value) val = val + '-' + add.unit.value
      if (add?.street?.value) val = val + ' ' + add.street.value
      if (add?.postal?.value) val = val + ' S(' + add.postal.value + ')'
    } else {
      let add = adddata
      if (add?.['line1']?.value) val = val + add['line1'].value
      if (add?.['line2']?.value) val = val + add['line2'].value
    }
    return val
  } else {
    return null
  }
}

const Step3: FC<any> = ({ updateApplication }) => {
  const dispatch = useDispatch()
  const history: any = useHistory()

  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertMsg, setAlertMsg] = useState<string>('')
  const [alertType, setAlertType] = useState<any>('primary')
  const [fileGroup, setFileGroup] = useState<any>([])

  const [createFileStorage, onSuccessCreateFileStorage] = useMutation(
    mutations.CREATE_OTHER_FILE_STORAGE
  )
  const [deleteFileStorage] = useMutation(mutations.DELETE_OTHER_FILE_STORAGE)

  const [createExternalDataStore, onSuccessCreateExternalDataStore] = useMutation(
    mutations.CREATE_EXTERNAL_DATA_STORE
  )

  const stateCompanyVerificationMethod = useSelector(
    (state: any) => state.loan.companyVerificationMethod
  )
  const companyVerificationMethod =
    (localStorage.getItem('companyVerificationMethod')
      ? Number(localStorage.getItem('companyVerificationMethod'))
      : false) || stateCompanyVerificationMethod
  
  // Safely extract person and entity with null/undefined handling
  const entityPersonData = useSelector((state: any) => state.loan.entityPerson) || {}
  const { person = null, entity = null }: any = entityPersonData
  const loanApplication = useSelector((state: any) => state.loan.loanApplication)

  const removeSingleFile = async (id: string) => {
    const _fileGroup = fileGroup.filter((o: any) => o.id !== id)
    setFileGroup(_fileGroup)
    await deleteFileStorage({
      variables: {
        where: { id: id },
      },
    })
  }
  //fileGroupValdiaiton

  function fileGroupValidation() {
    if (companyVerificationMethod === 2) {
      return fileGroup.some((f: any) => f.type === 'acra-details')
    }
    return true
  }

  const isValidFile = (event: any, fileTypes: string[], docType: string = '') => {
    const excelTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/octet-stream', // Some Excel files might have this type
      'application/zip', // .xlsx is actually a zip file
    ]

    const isExcelFile = (file: File) => {
      const fileName = file.name.toLowerCase()
      return (
        excelTypes.includes(file.type) ||
        fileName.endsWith('.xlsx') ||
        fileName.endsWith('.xls') ||
        file.type === ''
      ) // Sometimes Excel files might not have a type
    }

    const isPdfFile = (file: File) => {
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    }

    const isValidType = (file: File) => {
      if (fileTypes.includes('application/pdf') && isPdfFile(file)) return true
      if (
        (fileTypes.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
          fileTypes.includes('application/vnd.ms-excel')) &&
        isExcelFile(file)
      )
        return true
      return fileTypes.includes(file.type)
    }

    const checkType = [...event.target.files].some((file: File) => !isValidType(file))

    if (checkType) {
      const docTypeDisplay = getDocTypeDisplay(docType)

      setShowAlert(true)
      setAlertMsg(`Please upload a valid ${docTypeDisplay}`)
      setAlertType('primary')
      // Clear the file input
      event.target.value = ''
      event.target.files = null
      return false
    }
    return true
  }

  const getDocTypeDisplay = (docType: string): string => {
    switch (docType) {
      case 'acra-details':
        return 'ACRA Details'
      default:
        return 'File'
    }
  }

  const handleFileUpload = (event: any, type: string, fileType: string[] = ['application/pdf']) => {
    if (event.target.validity.valid) {
      let files = []
      let uploadDocs = []

      const isValid = isValidFile(event, fileType, type)

      if (isValid) {
        for (let file of event.target.files) {
          const isExists = fileGroup.some((f: any) => {
            return (
              cleanFileNameDynamic(f.file.filename) === cleanFileNameDynamic(file.name) &&
              f.type === type
            )
          })

          if (!isExists) {
            file.filename = file.name
            files.push({ type: type, file })
            uploadDocs.push({
              application: {
                connect: {
                  id: loanApplication.id,
                },
              },
              fileType: type,
              documentFile: {
                upload: file,
              },
              updatedByUserName: loanApplication?.applicantName,
            })
          } else {
            event.target.value = ''
            setShowAlert(true)
            setAlertMsg(`File already exists`)
            setAlertType('primary')
          }
        }

        if (files.length) {
          event.target.value = ''
          createFileStorage({
            variables: {
              data: uploadDocs,
            },
          })

          dispatch(fetchStart())
        }
      }
    }
  }

  useEffect(() => {
    if (onSuccessCreateFileStorage.called && !onSuccessCreateFileStorage.loading) {
      dispatch(fetchStop())
      if (onSuccessCreateFileStorage.data) {
        if (onSuccessCreateFileStorage.data) {
          const { createOtherStorages } = onSuccessCreateFileStorage.data
          let file = [...fileGroup]
          for (const document of createOtherStorages) {
            const _file = {
              id: document.id,
              type: document.fileType,
              file: document.documentFile,
            }
            file.push(_file)
          }

          setFileGroup(file)

          onSuccessCreateFileStorage.reset()
        }
      } else {
      }

      if (onSuccessCreateFileStorage.error) {
        const { networkError } = onSuccessCreateFileStorage.error
        let errorList = 'ERROR: '
        if (networkError && (networkError as ServerError)) {
          errorList =
            errorList +
            (networkError as ServerError as any).result.errors
              .map((error: any) => error.message)
              .join(', ')
        }
        setShowAlert(true)
        setAlertMsg(errorList)
        setAlertType('primary')
      }
    }
    // eslint-disable-next-line
  }, [
    onSuccessCreateFileStorage.loading,
    onSuccessCreateFileStorage.data,
    onSuccessCreateFileStorage.error,
    onSuccessCreateFileStorage.called,
  ])

  useEffect(() => {
    if (onSuccessCreateExternalDataStore.called && !onSuccessCreateExternalDataStore.loading) {
      dispatch(fetchStop())
      if (onSuccessCreateExternalDataStore.data) {
        history.push(`/application/${loanApplication.id}?step=4`)

        onSuccessCreateExternalDataStore.reset()
      } else {
      }

      if (onSuccessCreateExternalDataStore.error) {
        setShowAlert(true)
        setAlertMsg('error')
        setAlertType('primary')
        dispatch(fetchStop())
      }
    }
    //eslint-disable-next-line
  }, [onSuccessCreateExternalDataStore.loading])

  useEffect(() => {
    if (loanApplication?.otherStorage) {
      const { otherStorage } = loanApplication
      let file = []
      for (const document of otherStorage) {
        if (document?.fileType === 'acra-details') {
          const _file = {
            id: document.id,
            type: document.fileType,
            file: document.documentFile,
          }
          file.push(_file)
        }
      }
      setFileGroup(file)
    }
  }, [loanApplication, loanApplication?.otherStorage])

  // Check if person data is available when using SingPass verification
  useEffect(() => {
    if (companyVerificationMethod === 1 && !person) {
      setShowAlert(true)
      setAlertMsg('Personal details are not available from SingPass')
      setAlertType('danger')
    }
  }, [person, companyVerificationMethod])

  const initialValues: any = {
    uinfin: person?.uinfin?.value ?? '',
    name: person?.name?.value ?? '',
    sex: person?.sex?.desc ?? '',
    // race: person.race.desc,
    nationality: person?.nationality?.desc ?? '',
    dob: person?.dob?.value ?? '',
    email: person?.email?.value ?? '',
    mobileno: person && person?.mobileno
      ? `${person?.mobileno?.prefix?.value ?? ''}${person?.mobileno?.areacode?.value ?? ''}${person?.mobileno?.nbr?.value ?? ''}`
      : '',
    // regadd: person ? `${person.regadd.block?.value} ${person.regadd.building?.value} ${person.regadd.floor?.value} ${person.regadd.unit?.value} ${person.regadd.street?.value} ${person.regadd.postal?.value}` : '',
    regadd: person && person?.regadd ? toStr(person.regadd, 'ADDRESSLOCAL') : '',
    // housingtype: `${person.housingtype.desc} ${person.hdbtype.desc}`,
    // marital: person.marital.desc,
    // edulevel: person.edulevel.desc,
    residentialstatus: person?.residentialstatus?.desc ?? '',
    ownerprivate: person?.ownerprivate?.value ?? '',
    noahistory: person?.noahistory?.noas || [],

    entityName: entity ? entity?.['basic-profile']?.['entity-name']?.value : '',
    entityType: entity ? entity?.['basic-profile']?.['entity-type']?.desc : '',
    entityStatus: entity ? entity?.['basic-profile']?.['entity-status']?.value : '',
    businessExpiryDate: entity ? entity?.['basic-profile']['business-expiry-date']?.value : '',
    registrationDate: entity ? entity?.['basic-profile']['registration-date']?.value : '',
    countryOfIncorporation: entity
      ? entity?.['basic-profile']?.['country-of-incorporation']?.desc
      : '',
    businessConstitution: entity ? entity?.['basic-profile']?.['business-constitution']?.desc : '',
    // primarySSIC: entity ? `${entity['basic-profile']['primary-activity'].code}-${entity['basic-profile']['primary-activity'].desc}` : '',
    // secondarySSIC: entity ? `${entity['basic-profile']['secondary-activity'].code}-${entity['basic-profile']['secondary-activity'].desc}` : '',
    primarySSIC: entity ? toStr(entity, 'PRIMARYSSIC') : '',
    secondarySSIC: entity ? toStr(entity, 'SECONDARYSSIC') : '',
    ownership: entity ? entity['basic-profile']['ownership']?.desc : '',
    companyType: entity ? entity['basic-profile']['company-type']?.desc : '',
    uen: entity ? entity?.['basic-profile']?.['uen']?.value : '',
    enregadd: entity ? toStr(entity, 'ENTITYADDRESS') : '',

    financials: entity ? entity?.financials?.highlights : [],
    corppassContacts: entity ? entity?.['corppass-contacts']?.['corppass-contacts-list'] ?? [] : [],
    capitals: entity ? entity?.capitals?.['capitals-list'] ?? [] : [],
    shareholdersList: entity ? entity?.shareholders?.['shareholders-list'] : [],
    appointments: entity ? entity?.['appointments']?.['appointments-list'] : [],

    confirmCheckbox: false,
  }
  // Validation rules only apply to step 2, not step 3
  // Helper functions for validation
  const noSpecialChars = /^[a-zA-Z0-9\s\-.,()&'/#]*$/
  const noSpecialCharsMessage = 'Special characters are not allowed'

  // Check for leading or trailing spaces
  const checkForLeadingTrailingSpaces = (value: string | undefined) => {
    if (!value) return true
    return value === value.trim()
  }

  const validationForManual = {
    uen: Yup.string()
      .required('Company UEN is required')
      .test('unique', 'Invalid UEN. Kindly recheck the entered UEN', (value: any) =>
        validateUEN(value)
      ),
    entityName: Yup.string()
      .required('Entity Name is required')
      .min(2, 'Entity Name must be at least 2 characters')
      .max(100, 'Entity Name must be at most 100 characters')
      .test({
        name: 'entity-name-validation',
        test: function (value) {
          // Skip validation if field is empty
          if (!value || value.trim() === '') {
            return false // Will trigger required validation
          }

          // Check for leading or trailing spaces
          if (value !== value.trim()) {
            return this.createError({
              message: 'Entity Name cannot have leading or trailing spaces',
            })
          }

          // Check for special characters
          if (!noSpecialChars.test(value || '')) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Please enter a valid name. It should include letters.'
                  : noSpecialCharsMessage,
            })
          }

          // Check if it contains any numbers
          if (/\d/.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Please enter a valid name. Numbers are not allowed in Entity Name.'
                  : 'Numbers are not allowed in Entity Name',
            })
          }

          // Check for only spaces
          if (value.trim().length === 0) {
            return this.createError({
              message: 'Only spaces are not allowed',
            })
          }

          return true
        },
      }),

    entityType: Yup.string()
      .required('Entity Type is required')
      .test({
        name: 'entity-type-validation',
        test: function (value) {
          // Skip validation if field is empty
          if (!value || value.trim() === '') {
            return false // Will trigger required validation
          }

          // Check for leading or trailing spaces
          if (value !== value.trim()) {
            return this.createError({
              message: 'Entity Type cannot have leading or trailing spaces',
            })
          }

          // Check that it only contains letters and spaces
          if (!/^[A-Za-z\s]+$/.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Only letters are allowed. You can include spaces.'
                  : 'Only alphabetic letters are allowed',
            })
          }

          // Check for special characters (though this should be covered by the above regex)
          if (!noSpecialChars.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Only letters are allowed. You can include spaces.'
                  : noSpecialCharsMessage,
            })
          }

          // Check for only spaces
          if (value.trim().length === 0) {
            return this.createError({
              message: 'Only spaces are not allowed',
            })
          }

          return true
        },
      }),
    enregadd: Yup.string()
      .required('Registered Address is required')
      .min(5, 'Address must be at least 5 characters')
      .max(200, 'Address must be at most 200 characters')
      .test(
        'not-just-spaces',
        'Only spaces are not allowed',
        (value) => !!value && value.trim().length > 0
      )
      .test(
        'contains-alphabets',
        'Address cannot have only numbers or special characters',
        (value) => (value ? /[a-zA-Z]/.test(value) : false)
      )
      .test(
        'no-leading-trailing-spaces',
        'Registered Address cannot have leading or trailing spaces',
        checkForLeadingTrailingSpaces
      ),
    registrationDate: Yup.string()
      .required('Registration Date is required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date format must be YYYY-MM-DD')
      .test('is-valid-date', 'Please enter a valid date', (value) => {
        if (!value) return true
        // Parse the date and check if it's valid
        const date = new Date(value)
        if (isNaN(date.getTime())) return false

        // Verify that the parts match what was actually set
        // This catches cases like 2021-02-31 which would be converted to 2021-03-03 silently
        const [year, month, day] = value.split('-').map(Number)
        const parsedDate = new Date(date)
        return (
          parsedDate.getFullYear() === year &&
          parsedDate.getMonth() + 1 === month &&
          parsedDate.getDate() === day
        )
      })
      .test('not-future', 'Registration Date cannot be in the future', (value) => {
        if (!value) return true
        const date = new Date(value)
        return !isNaN(date.getTime()) && date <= new Date()
      }),

    primarySSIC: Yup.string()
      .required('Primary SSIC is required')
      .test(
        'no-leading-trailing-spaces',
        'Primary SSIC cannot have leading or trailing spaces',
        checkForLeadingTrailingSpaces
      )
      .matches(noSpecialChars, noSpecialCharsMessage)
      .test(
        'not-only-special-chars',
        'Only special characters are not allowed',
        (val) => !!val && /[A-Za-z0-9]/.test(val)
      ),
    secondarySSIC: Yup.string()
      .nullable()
      // Only apply validations if user has entered something
      .test({
        name: 'optional-validation',
        test: function (value) {
          // Skip all validations if field is empty
          if (!value || value.trim() === '' || companyVerificationMethod === 1) {
            return true
          }

          // Check for leading or trailing spaces
          if (value !== value.trim()) {
            return this.createError({
              message: 'Secondary SSIC cannot have leading or trailing spaces',
            })
          }

          // Check for special characters
          if (!noSpecialChars.test(value)) {
            return this.createError({
              message: noSpecialCharsMessage,
            })
          }

          // Check that it's not just special characters
          if (!/[A-Za-z0-9]/.test(value)) {
            return this.createError({
              message: 'Only special characters are not allowed',
            })
          }

          // Check that secondary SSIC doesn't match primary SSIC
          const primarySSIC = this.parent.primarySSIC
          if (value === primarySSIC) {
            return this.createError({
              message: 'Secondary SSIC should not be the same as Primary SSIC',
            })
          }

          return true
        },
      }),

    countryOfIncorporation: Yup.string()
      .required('Country of Incorporation is required')
      .test({
        name: 'country-of-incorporation-validation',
        test: function (value) {
          // Skip validation if field is empty (required validation will handle this)
          if (!value) {
            return true
          }

          // Check for only spaces
          if (value.trim().length === 0) {
            return this.createError({
              message: 'Only spaces are not allowed',
            })
          }

          if (value !== value.trim()) {
            return this.createError({
              message: 'Country of Incorporation cannot have leading or trailing spaces',
            })
          }

          // Check for special characters - use a direct regex check rather than the variable
          if (!/^[a-zA-Z0-9\s\-.,()&'/#]*$/.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Please enter a valid country name. It should include letters.'
                  : noSpecialCharsMessage,
            })
          }

          // Check if it contains any numbers
          if (/\d/.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Please enter a valid country name. Numbers are not allowed in Country of Incorporation.'
                  : 'Numbers are not allowed in Country of Incorporation',
            })
          }

          return true
        },
      }),

    companyType: Yup.string()
      .required('Company Type is required')
      .test({
        name: 'company-type-validation',
        test: function (value) {
          // Skip validation if field is empty (required validation will handle this)
          if (!value) {
            return true
          }

          // Check for only spaces
          if (value.trim().length === 0) {
            return this.createError({
              message: 'Only spaces are not allowed',
            })
          }

          if (value !== value.trim()) {
            return this.createError({
              message: 'Company Type cannot have leading or trailing spaces',
            })
          }

          // Check for special characters - use a direct regex check rather than the variable
          if (!/^[a-zA-Z0-9\s\-.,()&'/#]*$/.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Please enter a valid company type. It should include letters.'
                  : noSpecialCharsMessage,
            })
          }

          // Check if it contains any numbers
          if (/\d/.test(value)) {
            return this.createError({
              message:
                companyVerificationMethod === 2
                  ? 'Please enter a valid company type. Numbers are not allowed in Company Type.'
                  : 'Numbers are not allowed in Company Type',
            })
          }

          return true
        },
      }),
  }

  const validationSchema = Yup.object().shape({
    ...validationForManual,
    mobileno:
      companyVerificationMethod === 1
        ? Yup.string()
            .required('Mobile number is required')
            .test('mobileValidation', 'Please enter valid Singapore number', (value: any) => {
              if (!value) return false
              const number = parsePhoneNumberWithError(value, 'SG')
              // Use libphonenumber-js to validate phone number format
              return isValidPhoneNumber(value, 'SG') && number.isValid() && number.country === 'SG'
            })
        : Yup.string().nullable(),
    email:
      companyVerificationMethod === 1
        ? Yup.string().required('Email Address is required').email('Enter Valid Email Address')
        : Yup.string().nullable(),
    confirmCheckbox: Yup.bool().oneOf([true], 'Please select checkbox to move ahead'),
  })

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validationSchema,
    onSubmit: (values: any, { setStatus, setSubmitting }: any) => {
      const { confirmCheckbox, ...rest } = values
      const trimmedValues = Object.fromEntries(
        Object.entries(values).map(([key, val]) => [
          key,
          typeof val === 'string' ? val.trim() : val,
        ])
      )

      let finalData: any
      if (companyVerificationMethod === 2) {
        const filePaths = fileGroup?.map((f: any) => f.file?.url)
        finalData = {
          uen: rest.uen,
          enregadd: rest.enregadd,
          primarySSIC: rest.primarySSIC,
          secondarySSIC: rest.secondarySSIC,
          ownership: rest.ownership,
          companyType: rest.companyType,
          entityName: rest.entityName,
          entityStatus: rest.entityStatus,
          entityType: rest.entityType,
          countryOfIncorporation: rest.countryOfIncorporation,
          businessConstitution: rest.businessConstitution,
          businessExpiryDate: rest.businessExpiryDate,
          registrationDate: rest.registrationDate,
          acraDetails: filePaths,
        }
      } else {
        finalData = {
          ...rest,
        }
      }
      dispatch(fetchStartWithForward())

      createExternalDataStore({
        variables: {
          data: {
            data: finalData,
            application: {
              connect: {
                id: loanApplication?.id,
              },
            },
            type: 'singpass',
          },
        },
      })

      updateApplication({
        variables: {
          data: {
            // applicationCompleteStep: 3
            experianConsent: true,
            mibCompanyName: trimmedValues?.entityName || '',
            mibCompanyUEN: trimmedValues?.uen || '',
          },
          where: { id: loanApplication.id },
        },
      })

      // Update the Redux store with the latest fileGroup data
      if (companyVerificationMethod === 2) {
        const updatedLoanApplication = {
          ...loanApplication,
          otherStorage: fileGroup.map((file: any) => ({
            id: file.id,
            fileType: file.type,
            documentFile: file.file,
            __typename: 'OtherStorage',
          })),
        }
        dispatch(setLoanApplication(updatedLoanApplication))
        dispatch(setEntityData(formatEntityDataForRedux(finalData)))
      } else {
        dispatch(setLoanApplication(loanApplication))
      }

      dispatch(setSingPassInfo(finalData))
    },
  })

  const financialHeaders =
    formik.values.financials?.length > 0
      ? [...financialHead, ...Object.keys(formik.values.financials[0])].reduce(
          (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
          []
        )
      : financialHead

  function backLink() {
    dispatch(setLoanStep(2))
    history.replace(`/application/${loanApplication.id}?step=2`)
  }

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      <form onSubmit={formik.handleSubmit}>
        {companyVerificationMethod === 1 && (
          <>
            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Personal Information</h2>
                <hr className='text-gray-400' />
              </div>
              <div className='row mb-10'>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    NRIC/FIN
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='uinfin'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={formik.values.uinfin}
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.uinfin && formik.errors.uinfin && (
                      <div className='text-danger mt-2'>{formik.errors.uinfin}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Name
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='name'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className='text-danger mt-2'>{formik.errors.name}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Sex
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='sex'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={formik.values.sex}
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.sex && formik.errors.sex && (
                      <div className='text-danger mt-2'>{formik.errors.sex}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Date of birth
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='dob'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={formik.values.dob}
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.dob && formik.errors.dob && (
                      <div className='text-danger mt-2'>{formik.errors.dob}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Mobile number
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='mobileno'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={
                        companyVerificationMethod === 1
                          ? formik.values.mobileno || 'N/A'
                          : formik.values.mobileno
                      }
                      onChange={formik.handleChange}
                      // disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.mobileno && formik.errors.mobileno && (
                      <div className='text-danger mt-2'>{formik.errors.mobileno}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Email
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='email'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={
                        companyVerificationMethod === 1
                          ? formik.values.email || 'N/A'
                          : formik.values.email
                      }
                      onChange={formik.handleChange}
                      // disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className='text-danger mt-2'>{formik.errors.email}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Nationality
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='nationality'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={
                        companyVerificationMethod === 1
                          ? formik.values.nationality || 'N/A'
                          : formik.values.nationality
                      }
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.nationality && formik.errors.nationality && (
                      <div className='text-danger mt-2'>{formik.errors.nationality}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Residential Status
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='residentialstatus'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={
                        companyVerificationMethod === 1
                          ? formik.values.residentialstatus || 'N/A'
                          : formik.values.residentialstatus
                      }
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.residentialstatus && formik.errors.residentialstatus && (
                      <div className='text-danger mt-2'>{formik.errors.residentialstatus}</div>
                    )}
                  </div>
                </div>

                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Registered Address
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='regadd'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={
                        companyVerificationMethod === 1
                          ? formik.values.regadd || 'N/A'
                          : formik.values.regadd
                      }
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.regadd && formik.errors.regadd && (
                      <div className='text-danger mt-2'>{formik.errors.regadd}</div>
                    )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label required'>
                    Owner Private
                  </label>
                  <div className='col-sm-12'>
                    <input
                      name='ownerprivate'
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      value={
                        companyVerificationMethod === 1
                          ? formik.values.ownerprivate || 'N/A'
                          : formik.values.ownerprivate
                      }
                      onChange={formik.handleChange}
                      disabled={companyVerificationMethod === 1}
                    />
                    {formik.touched.ownerprivate && formik.errors.ownerprivate && (
                      <div className='text-danger mt-2'>{formik.errors.ownerprivate}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Notice of Assessment History</h2>
                <hr className='text-gray-400' />
              </div>

              <div className='table-wrapper px-2'>
                {formik.values.noahistory.length > 0 ? (
                  <table className='table table-bordered text-align-center w-100 px-2'>
                    <thead className='table-header p-2'>
                      <tr className='text-dark'>
                        {formik.values.noahistory.length > 0 &&
                          [...historyHead, ...Object.keys(formik.values.noahistory[0])]
                            .reduce(
                              (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                              []
                            )
                            .map((key: any, i: number) => {
                              let tableHead = ''
                              if (key === 'taxclearance') tableHead = 'tax-clearance'
                              else if (key === 'yearofassessment') tableHead = 'year-of-assessment'
                              else tableHead = key

                              const value = tableHead
                                .replace(/-/g, ' ')
                                .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                              return (
                                <th className='min-w-150px' key={i}>
                                  {value}
                                </th>
                              )
                            })}
                      </tr>
                    </thead>
                    <tbody>
                      {formik.values.noahistory.length > 0 &&
                        Object.values(formik.values.noahistory).map((list: any, i) => {
                          const headList: any = [
                            ...historyHead,
                            ...Object.keys(formik.values.noahistory[0]),
                          ].reduce(
                            (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                            []
                          )
                          return (
                            <tr key={i}>
                              {headList.map((head: any, i: number) => {
                                let value = `${list[head].value}`

                                if (
                                  head === 'amount' ||
                                  head === 'interest' ||
                                  head === 'employment'
                                )
                                  value = value
                                    .toString()
                                    .replace(/(\d)(?=(\d{3})+(\.(\d){0,2})*$)/g, '$1,')
                                if (head === 'amount' || head === 'interest') value = 'SG$ ' + value
                                else value = value.toString()
                                return <td key={i}>{value}</td>
                              })}
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                ) : (
                  <p className='fs-5'>No data available</p>
                )}
              </div>
            </div>
          </>
        )}
        {companyVerificationMethod === 2 && (
          <div className='d-flex align-items-center gap-5 mb-10'>
            <svg
              onClick={backLink}
              style={{ cursor: 'pointer' }}
              width='36'
              height='36'
              viewBox='0 0 36 36'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <rect width='36' height='36' rx='18' fill='#DBE2E3' />
              <path
                d='M11 18H25'
                stroke='var(--primary-color)'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
              <path
                d='M11 18L17 24'
                stroke='var(--primary-color)'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
              <path
                d='M11 18L17 12'
                stroke='var(--primary-color)'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
              />
            </svg>

            <span style={{ color: 'var(--primary-color)', fontWeight: 600, fontSize: '24px' }}>
              Fill Out Details Manually
            </span>
          </div>
        )}
        <div className='cardNew'>
          <div className='section-heading'>
            <h2>{companyVerificationMethod === 2 ? 'Company' : 'Corporate'} Information</h2>
            <hr className='text-gray-400' />
          </div>
          <div className='row mb-10'>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                UEN
              </label>
              <div className='col-sm-12'>
                <input
                  name='uen'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1 ? formik.values.uen || 'N/A' : formik.values.uen
                  }
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.uen && formik.errors.uen && (
                  <div className='text-danger mt-2'>{formik.errors.uen}</div>
                )}
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Entity Name
              </label>
              <div className='col-sm-12'>
                <input
                  name='entityName'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.entityName || 'N/A'
                      : formik.values.entityName
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.entityName && formik.errors.entityName && (
                  <div className='text-danger mt-2'>{formik.errors.entityName}</div>
                )}
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Entity Type
              </label>
              <div className='col-sm-12'>
                <input
                  name='entityType'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.entityType || 'N/A'
                      : formik.values.entityType
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.entityType && formik.errors.entityType && (
                  <div className='text-danger mt-2'>{formik.errors.entityType}</div>
                )}
              </div>
            </div>
            {companyVerificationMethod === 1 && (
              <div className='col-md-6 mb-5'>
                <label htmlFor='' className='input-field-label required'>
                  Entity Status
                </label>
                <div className='col-sm-12'>
                  <input
                    name='entityStatus'
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    value={
                      companyVerificationMethod === 1
                        ? formik.values.entityStatus || 'N/A'
                        : formik.values.entityStatus
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={companyVerificationMethod === 1}
                  />
                  {formik.touched.entityStatus && formik.errors.entityStatus && (
                    <div className='text-danger mt-2'>{formik.errors.entityStatus}</div>
                  )}
                </div>
              </div>
            )}
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Registered Address
              </label>
              <div className='col-sm-12'>
                <input
                  name='enregadd'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.enregadd || 'N/A'
                      : formik.values.enregadd
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.enregadd && formik.errors.enregadd && (
                  <div className='text-danger mt-2'>{formik.errors.enregadd}</div>
                )}
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Registration Date
              </label>
              <div className='col-sm-12'>
                <input
                  name='registrationDate'
                  type='text'
                  placeholder='YYYY-MM-DD'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.registrationDate || 'N/A'
                      : formik.values.registrationDate
                  }
                  onChange={(e) => {
                    const input = e.target.value
                    // Remove any non-digit characters
                    const digitsOnly = input.replace(/\D/g, '')

                    // Format with hyphens as user types
                    let formattedDate = ''
                    if (digitsOnly.length > 0) {
                      // Add year part (up to 4 digits)
                      formattedDate += digitsOnly.substring(0, Math.min(4, digitsOnly.length))

                      // Add month part with hyphen
                      if (digitsOnly.length > 4) {
                        formattedDate +=
                          '-' + digitsOnly.substring(4, Math.min(6, digitsOnly.length))
                      }

                      // Add day part with hyphen
                      if (digitsOnly.length > 6) {
                        formattedDate +=
                          '-' + digitsOnly.substring(6, Math.min(8, digitsOnly.length))
                      }
                    }

                    // Update formik value with formatted date
                    formik.setFieldValue('registrationDate', formattedDate)
                  }}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                  maxLength={10} // YYYY-MM-DD is 10 characters
                />
                {formik.touched.registrationDate && formik.errors.registrationDate && (
                  <div className='text-danger mt-2'>{formik.errors.registrationDate}</div>
                )}
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Primary SSIC
              </label>
              <div className='col-sm-12'>
                <input
                  name='primarySSIC'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.primarySSIC || 'N/A'
                      : formik.values.primarySSIC
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.primarySSIC && formik.errors.primarySSIC && (
                  <div className='text-danger mt-2'>{formik.errors.primarySSIC}</div>
                )}
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Secondary SSIC (optional)
              </label>
              <div className='col-sm-12'>
                <input
                  name='secondarySSIC'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.secondarySSIC || 'N/A'
                      : formik.values.secondarySSIC
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.secondarySSIC && formik.errors.secondarySSIC && (
                  <div className='text-danger mt-2'>{formik.errors.secondarySSIC}</div>
                )}
              </div>
            </div>
            {companyVerificationMethod === 1 && (
              <div className='col-md-6 mb-5'>
                <label htmlFor='' className='input-field-label required'>
                  Business Expiry Date
                </label>
                <div className='col-sm-12'>
                  <input
                    name='businessExpiryDate'
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    value={
                      companyVerificationMethod === 1
                        ? formik.values.businessExpiryDate || 'N/A'
                        : formik.values.businessExpiryDate
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={companyVerificationMethod === 1}
                  />
                  {formik.touched.businessExpiryDate && formik.errors.businessExpiryDate && (
                    <div className='text-danger mt-2'>{formik.errors.businessExpiryDate}</div>
                  )}
                </div>
              </div>
            )}

            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Country Of Incorporation
              </label>
              <div className='col-sm-12'>
                <input
                  name='countryOfIncorporation'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.countryOfIncorporation || 'N/A'
                      : formik.values.countryOfIncorporation
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.countryOfIncorporation && formik.errors.countryOfIncorporation && (
                  <div className='text-danger mt-2'>{formik.errors.countryOfIncorporation}</div>
                )}
              </div>
            </div>

            {companyVerificationMethod === 1 && (
              <div className='col-md-6 mb-5'>
                <label htmlFor='' className='input-field-label required'>
                  Business Constitution
                </label>
                <div className='col-sm-12'>
                  <input
                    name='businessConstitution'
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    value={
                      companyVerificationMethod === 1
                        ? formik.values.businessConstitution || 'N/A'
                        : formik.values.businessConstitution
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={companyVerificationMethod === 1}
                  />
                  {formik.touched.businessConstitution && formik.errors.businessConstitution && (
                    <div className='text-danger mt-2'>{formik.errors.businessConstitution}</div>
                  )}
                </div>
              </div>
            )}

            {companyVerificationMethod === 1 && (
              <div className='col-md-6 mb-5'>
                <label htmlFor='' className='input-field-label required'>
                  Ownership
                </label>
                <div className='col-sm-12'>
                  <input
                    name='ownership'
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    value={
                      companyVerificationMethod === 1
                        ? formik.values.ownership || 'N/A'
                        : formik.values.ownership
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={companyVerificationMethod === 1}
                  />
                  {formik.touched.ownership && formik.errors.ownership && (
                    <div className='text-danger mt-2'>{formik.errors.ownership}</div>
                  )}
                </div>
              </div>
            )}

            <div className='col-12 mb-4'>
              <label htmlFor='' className='input-field-label required'>
                Company Type
              </label>
              <div className='col-sm-12'>
                <input
                  name='companyType'
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  value={
                    companyVerificationMethod === 1
                      ? formik.values.companyType || 'N/A'
                      : formik.values.companyType
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={companyVerificationMethod === 1}
                />
                {formik.touched.companyType && formik.errors.companyType && (
                  <div className='text-danger mt-2'>{formik.errors.companyType}</div>
                )}
              </div>
            </div>
            {companyVerificationMethod === 2 && (
              <>
                <hr className='text-gray-400' />
                <div className='col-12 mb-4'>
                  <label htmlFor='' className='input-field-label required'>
                    Upload ACRA
                  </label>
                  <div
                    className='uploadBoxCoApp'
                    style={{
                      border: '1px dashed var(--primary-color)',
                      background: 'var(--primary-light-color)',
                      minHeight: '150px',
                      fontSize: '14px',
                      marginBottom: '15px',
                      borderRadius: '10px',
                      alignItems: 'center',
                    }}
                    onDrop={(e: React.DragEvent) => {
                      e.preventDefault()
                      e.stopPropagation()
                      const files = e.dataTransfer.files
                      if (files.length) {
                        const event = { target: { files, validity: { valid: true } } }
                        handleFileUpload(event, 'acra-details', ['application/pdf'])
                      }
                    }}
                    onDragOver={(e: React.DragEvent) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onDragLeave={(e: React.DragEvent) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                  >
                    <Stack
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      gap={2} // Increased gap for better spacing
                    >
                      <div className='upload mb-4'>
                        <input
                          id='upload-acra-details'
                          type='file'
                          multiple
                          accept='application/pdf'
                          onChange={(e: any) =>
                            handleFileUpload(e, 'acra-details', ['application/pdf'])
                          }
                          onDrop={(e: React.DragEvent) => {
                            e.preventDefault()
                            e.stopPropagation()
                            const files = e.dataTransfer.files
                            if (files.length) {
                              const event = { target: { files, validity: { valid: true } } }
                              handleFileUpload(event, 'acra-details', ['application/pdf'])
                            }
                          }}
                          onDragOver={(e: React.DragEvent) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                          onDragLeave={(e: React.DragEvent) => {
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                        ></input>
                        <label
                          htmlFor='upload-acra-details'
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            className='file-uploader'
                            style={{
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <UploadIcon color='var(--primary-color)' />

                            <span className='dragDropTitle'>Drag & Drop ACRA Document</span>
                          </div>
                          <div className='file-uploader-sub'>Supported formats: PDF</div>
                        </label>
                      </div>
                    </Stack>
                  </div>
                  <div className='uploadedCapsulesWrap'>
                    <Stack
                      className='uploadedCapsules gridCapsules'
                      style={{
                        display: 'grid', // Change to grid layout
                        gridTemplateColumns: 'repeat(6, 1fr)', // Create 6 equal columns
                        gap: '12px', // Adjusted gap for better spacing
                        fontSize: '12px',
                        minWidth: '100%',
                      }}
                    >
                      {fileGroup && fileGroup.length > 0 && (
                        <>
                          {fileGroup
                            .filter(({ type }: any) => type === 'acra-details')
                            .map(({ file, type, id }: any, i: number) => {
                              const filename = file.filename.split('.')
                              const name = filename[0].substring(0, 12)
                              const extension = filename[1]

                              return (
                                <div
                                  key={i}
                                  style={{
                                    fontSize: 14,
                                    color: '#616B75',
                                    background: '#F9FAFA',
                                    borderRadius: '20px',
                                    border: '1px solid #E0E0E0',
                                    padding: '8px 16px',
                                    display: 'flex', // Use flex to position items
                                    justifyContent: 'space-between', // Space between text and icon
                                    alignItems: 'center', // Center items vertically
                                    width: '100%', // Set to 100% to fill the grid cell
                                  }}
                                  title={file.filename}
                                >
                                  <span>{`${name}...${extension}`}</span>
                                  <Xicon
                                    onClick={() => removeSingleFile(id)}
                                    className='cursor-pointer'
                                  />
                                </div>
                              )
                            })}
                        </>
                      )}
                    </Stack>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {companyVerificationMethod === 1 && (
          <>
            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Financials</h2>
                <hr className='text-gray-400' />
              </div>
              <div className='table-wrapper px-2'>
                <table className='table table-bordered align-self-center w-100 px-2'>
                  <thead className='table-header p-2'>
                    <tr className='text-black'>
                      {financialHeaders.map((tableHead: any, i: number) => {
                        const value = tableHead
                          .replace(/-/g, ' ')
                          .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())

                        return (
                          <th key={i} className='ps-2' style={{ minWidth: '250px' }}>
                            {value}
                          </th>
                        )
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {formik.values?.financials && formik.values?.financials?.length > 0 ? (
                      Object.values(formik.values.financials).map((list: any, i: number) => (
                        <tr key={i}>
                          {financialHeaders.map((head: any, i: number) => {
                            let value: any
                            if (head === 'currency') {
                              value = `${list?.[head]?.desc ?? 'N/A'}`
                            } else if (
                              head === 'current-period-end-date' ||
                              head === 'current-period-start-date'
                            ) {
                              value = `${list?.[head]?.value ?? 'N/A'}`
                            } else {
                              value = `${
                                list?.[head]?.value
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? 'N/A'
                              }`
                            }

                            return (
                              <td key={i} className='ps-2'>
                                {value || 'N/A'}
                              </td>
                            )
                          })}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        {financialHeaders.map((key: string, i: number) => (
                          <td key={i}>N/A</td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Corppass Contacts</h2>
                <hr className='text-gray-400' />
              </div>
              <div className='table-wrapper px-2'>
                <table className='table table-bordered  table-hover align-middle w-100'>
                  <thead className='table-header p-2'>
                    <tr className='text-dark'>
                      {formik.values.corppassContacts &&
                        formik.values.corppassContacts.length > 0 &&
                        [...corppassHead, ...Object.keys(formik.values.corppassContacts[0])]
                          .reduce(
                            (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                            []
                          )
                          .map((key: any, i: number) => {
                            let tableHead = ''
                            if (key === 'corppass-mobileno') tableHead = 'mobile-number'
                            else tableHead = key
                            const value = tableHead
                              .replace(/-/g, ' ')
                              .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                            return (
                              <th className='min-w-150px ps-2' key={i}>
                                {value}
                              </th>
                            )
                          })}
                    </tr>
                  </thead>
                  <tbody>
                    {formik.values.corppassContacts &&
                      formik.values.corppassContacts.length > 0 &&
                      Object.values(formik.values.corppassContacts).map((list: any, i) => {
                        const headList: any = [
                          ...corppassHead,
                          ...Object.keys(formik.values.corppassContacts[0]),
                        ].reduce(
                          (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                          []
                        )
                        return (
                          <tr key={i}>
                            {headList.map((head: any, i: number) => {
                              let value: any
                              if (head === 'person-reference') {
                                value = (
                                  <div>
                                    <div>
                                      <span className='fw-bold'>IdNo: </span>
                                      {list?.[head]?.idno?.value ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Nationality: </span>
                                      {list?.[head]?.nationality?.desc ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Person name: </span>
                                      {list?.[head]?.['person-name']?.value ?? 'N/A'}
                                    </div>
                                  </div>
                                )
                              } else if (head === 'corppass-email')
                                value = `${list?.[head]?.value ?? 'N/A'}`
                              else if (head === 'corppass-mobileno')
                                value = `${list?.[head]?.value ?? 'N/A'}`
                              return (
                                <td key={i} className='ps-2'>
                                  {value || 'N/A'}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>

                {formik.values?.corppassContacts &&
                  formik.values?.corppassContacts?.length === 0 && (
                    <table className='table table-bordered text-align-center w-100 px-2'>
                      <thead className='table-header p-2'>
                        <tr className='text-dark'>
                          {corppassHead.map((key: any, i: number) => {
                            let tableHead = ''
                            if (key === 'corppass-mobileno') tableHead = 'mobile-number'
                            else tableHead = key
                            const value = tableHead
                              .replace(/-/g, ' ')
                              .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                            return (
                              <th className='min-w-150px ps-2' key={i}>
                                {value}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {corppassHead.map((key: string, i: number) => {
                            return (
                              <Fragment key={i}>
                                <td className='ps-2'>N/A</td>
                              </Fragment>
                            )
                          })}
                        </tr>
                      </tbody>
                    </table>
                  )}
              </div>
            </div>

            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Capitals</h2>
                <hr className='text-gray-400' />
              </div>
              <div className='table-wrapper px-2'>
                <table className='table table-bordered text-align-center w-100 px-2'>
                  <thead className='table-header p-2'>
                    <tr className='text-dark'>
                      {formik.values.capitals &&
                        formik.values.capitals.length > 0 &&
                        [...capitalHead, ...Object.keys(formik.values.capitals[0])]
                          .reduce(
                            (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                            []
                          )
                          .map((tableHead: any, i: number) => {
                            const value = tableHead
                              .replace(/-/g, ' ')
                              .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                            return (
                              <th className='min-w-150px ms-2' key={i}>
                                {value}
                              </th>
                            )
                          })}
                    </tr>
                  </thead>
                  <tbody>
                    {formik.values.capitals &&
                      formik.values.capitals.length > 0 &&
                      Object.values(formik.values.capitals).map((list: any, i) => {
                        const headList: any = [
                          ...capitalHead,
                          ...Object.keys(formik.values.capitals[0]),
                        ].reduce(
                          (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                          []
                        )
                        return (
                          <tr key={i}>
                            {headList.map((head: any, i: number) => {
                              let value: any
                              if (
                                head === 'capital-type' ||
                                head === 'currency' ||
                                head === 'currency'
                              )
                                value = `${list?.[head]?.desc ?? 'N/A'}`
                              else if (
                                head === 'issued-capital-amount' ||
                                head === 'paid-up-capital-amount' ||
                                head === 'share-allotted-amount'
                              )
                                value = `SG$ ${
                                  list?.[head]?.value
                                    .toString()
                                    .replace(/(\d)(?=(\d{3})+(\.(\d){0,2})*$)/g, '$1,') ?? 'N/A'
                                }`
                              return (
                                <td key={i} className='ms-2'>
                                  {value || 'N/A'}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>

                {formik.values?.capitals && formik.values?.capitals?.length === 0 && (
                  <table className='table table-bordered text-align-center w-100 px-2'>
                    <thead className='table-header p-2'>
                      <tr className='text-dark'>
                        {capitalHead.map((tableHead: any, i: number) => {
                          const value = tableHead
                            .replace(/-/g, ' ')
                            .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                          return (
                            <th className='min-w-150px' key={i}>
                              {value}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {capitalHead.map((key: string, i: number) => {
                          return (
                            <Fragment key={i}>
                              <td>N/A</td>
                            </Fragment>
                          )
                        })}
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Shareholders List</h2>
                <hr className='text-gray-400' />
              </div>
              <div className='table-wrapper px-2'>
                <table className='table table-bordered text-align-center w-100 px-2'>
                  <thead className='table-header p-2'>
                    <tr className='text-dark'>
                      {formik.values.shareholdersList &&
                        formik.values.shareholdersList.length > 0 &&
                        [...shareholderHead, ...Object.keys(formik.values.shareholdersList[0])]
                          .reduce(
                            (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                            []
                          )
                          .map((key: any, i: number) => {
                            let tableHead = ''
                            if (key === 'corppass-mobileno') tableHead = 'mobile-number'
                            else tableHead = key
                            const value = tableHead
                              .replace(/-/g, ' ')
                              .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                            return (
                              <th className='min-w-150px' key={i}>
                                {value}
                              </th>
                            )
                          })}
                    </tr>
                  </thead>
                  <tbody>
                    {formik.values.shareholdersList &&
                      formik.values.shareholdersList.length > 0 &&
                      Object.values(formik.values.shareholdersList).map((list: any, i) => {
                        const headList: any = [
                          ...shareholderHead,
                          ...Object.keys(formik.values.shareholdersList[0]),
                        ].reduce(
                          (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                          []
                        )
                        return (
                          <tr key={i}>
                            {headList.map((head: any, i: number) => {
                              let value: any
                              // if(head === 'person-reference') value = `IdNo: ${list[head].idno.value} Nationality: ${list[head].nationality.desc} Person name: ${list[head]['person-name'].value}`
                              if (head === 'person-reference') {
                                value = (
                                  <div>
                                    <div>
                                      <span className='fw-bold'>IdNo: </span>
                                      {list?.[head]?.idno?.value ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Nationality: </span>
                                      {list?.[head]?.nationality?.desc ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Person name: </span>
                                      {list?.[head]?.['person-name']?.value ?? 'N/A'}
                                    </div>
                                  </div>
                                )
                              } else if (head === 'entity-reference') {
                                value = (
                                  <div>
                                    <div>
                                      <span className='fw-bold'>Name: </span>
                                      {list?.[head]?.['entity-name']?.value ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Type: </span>
                                      {list?.[head]?.['entity-type']?.desc ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>UEN: </span>
                                      {list?.[head]?.uen?.value ?? 'N/A'}
                                    </div>
                                  </div>
                                )
                              } else if (
                                head === 'category' ||
                                head === 'currency' ||
                                head === 'share-type'
                              )
                                value = `${list?.[head]?.desc ?? 'N/A'}`
                              else if (head === 'allocation')
                                value = `SG$  ${list?.[head]?.value
                                  .toString()
                                  .replace(/(\d)(?=(\d{3})+(\.(\d){0,2})*$)/g, '$1,')}`
                              else if (head === 'corppass-email' || head === 'corppass-mobileno')
                                value = `${list?.[head]?.value ?? 'N/A'}`
                              return <td key={i}>{value}</td>
                            })}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>

                {formik.values?.shareholdersList &&
                  formik.values?.shareholdersList?.length === 0 && (
                    <table className='table table-bordered text-align-center w-100 px-2'>
                      <thead className='table-header p-2'>
                        <tr className='text-dark'>
                          {shareholderHead.map((key: any, i: number) => {
                            let tableHead = ''
                            if (key === 'corppass-mobileno') tableHead = 'mobile-number'
                            else tableHead = key
                            const value = tableHead
                              .replace(/-/g, ' ')
                              .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                            return (
                              <th className='min-w-150px' key={i}>
                                {value}
                              </th>
                            )
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {shareholderHead.map((key: string, i: number) => {
                            return (
                              <Fragment key={i}>
                                <td>N/A</td>
                              </Fragment>
                            )
                          })}
                        </tr>
                      </tbody>
                    </table>
                  )}
              </div>
            </div>

            <div className='cardNew'>
              <div className='section-heading'>
                <h2>Appointments</h2>
                <hr className='text-gray-400' />
              </div>
              <div className='table-wrapper px-2'>
                <table className='table table-bordered text-align-center w-100 px-2'>
                  <thead className='table-header p-2'>
                    <tr className='text-dark'>
                      {formik.values.appointments &&
                        formik.values.appointments.length > 0 &&
                        [...appointmentHead, ...Object.keys(formik.values.appointments[0])]
                          .reduce(
                            (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                            []
                          )
                          .map((key: any, i: number) => {
                            let tableHead = ''
                            if (key === 'corppass-mobileno') tableHead = 'mobile-number'
                            else tableHead = key
                            const value = tableHead
                              .replace(/-/g, ' ')
                              .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                            return (
                              <th className='min-w-150px' key={i}>
                                {value}
                              </th>
                            )
                          })}
                    </tr>
                  </thead>
                  <tbody>
                    {formik.values.appointments &&
                      formik.values.appointments.length > 0 &&
                      Object.values(formik.values.appointments).map((list: any, i) => {
                        const headList: any = [
                          ...appointmentHead,
                          ...Object.keys(formik.values.appointments[0]),
                        ].reduce(
                          (acc: any, curr: any) => (acc.includes(curr) ? acc : [...acc, curr]),
                          []
                        )

                        return (
                          <tr key={i}>
                            {headList.map((head: any, i: number) => {
                              let value: any
                              // if(head === 'person-reference') value = `IdNo: ${list[head].idno.value} Nationality: ${list[head].nationality.desc} Person name: ${list[head]['person-name'].value}`
                              if (head === 'person-reference') {
                                value = (
                                  <div>
                                    <div>
                                      <span className='fw-bold'>IdNo: </span>
                                      {list?.[head]?.idno?.value ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Nationality: </span>
                                      {list?.[head]?.nationality?.desc ?? 'N/A'}
                                    </div>
                                    <div>
                                      <span className='fw-bold'>Person name: </span>
                                      {list?.[head]?.['person-name']?.value ?? 'N/A'}
                                    </div>
                                  </div>
                                )
                              } else if (head === 'position')
                                value = `${list?.[head]?.desc ?? 'N/A'}`
                              else if (head === 'appointment-date')
                                value = `${list?.[head]?.value ?? 'N/A'}`
                              else if (head === 'corppass-email')
                                value = `${list?.[head]?.value ?? 'N/A'}`
                              else if (head === 'category') value = `${list?.[head]?.desc ?? 'N/A'}`
                              else if (head === 'corppass-mobileno')
                                value = `${list?.[head]?.value ?? 'N/A'}`
                              else value = ''
                              return <td key={i}>{value || 'N/A'}</td>
                            })}
                          </tr>
                        )
                      })}
                  </tbody>
                </table>

                {formik.values?.appointments && formik.values?.appointments?.length === 0 && (
                  <table className='table table-bordered text-align-center w-100 px-2'>
                    <thead className='table-header p-2'>
                      <tr className='text-dark'>
                        {appointmentHead.map((key: any, i: number) => {
                          let tableHead = ''
                          if (key === 'corppass-mobileno') tableHead = 'mobile-number'
                          else tableHead = key
                          const value = tableHead
                            .replace(/-/g, ' ')
                            .replace(/(?: |\b)(\w)/g, (key: any, p1: any) => key.toUpperCase())
                          return (
                            <th className='min-w-150px' key={i}>
                              {value}
                            </th>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {appointmentHead.map((key: string, i: number) => {
                          return (
                            <Fragment key={i}>
                              <td>N/A</td>
                            </Fragment>
                          )
                        })}
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}

        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Borrower Consent</h2>
            <hr className='text-gray-400' />
          </div>
          <div className='mt-5'>
            <label className='form-check form-check-inline form-check-solid me-5'>
              <input
                name='confirmCheckbox'
                type='checkbox'
                className={`form-check-input ${
                  (formik.submitCount > 0 || formik.errors.confirmCheckbox) &&
                  !formik.values.confirmCheckbox
                    ? 'is-invalid'
                    : ''
                }`}
                checked={formik.values.confirmCheckbox}
                onChange={formik.handleChange}
              />
              <span className='ps-2 fs-4 text-justified'>
                <b>By submitting this information, I hereby:</b> <br />
                <br />
                <ul className='text-justify gap-4'>
                  <li className='mb-4'>
                    Confirm that I am submitting a formal loan application, which will be processed
                    by CCR Partners, CrediLinq A.I Pte Ltd. and their respective affiliates and
                    partners consulting credit bureaus, and to the sharing of my personal and
                    business data between them for the purpose of processing the relevant loan
                    application.
                  </li>
                  <li className='mb-4'>
                    Declare that all information provided is true, complete, and accurate to the
                    best of my knowledge.
                  </li>
                  <li className='mb-4'>
                    Authorise the appointed Channel Partner to act on my behalf in relation to this
                    loan application, including the submission of all required information and
                    documentation.
                  </li>
                  <li className='mb-4'>
                    Give my explicit consent to CCR Partners and its authorized personnel to contact
                    me using the contact details provided, including but not limited to email, phone
                    call, WhatsApp, and SMS/text message. Such communication will pertain solely to
                    the status and processing of the loan application.
                  </li>
                </ul>
              </span>
            </label>
          </div>
          <div className='mt-5'>
            <span className='ps-2 fs-4 text-justified' style={{ fontWeight: 700 }}>
              CCR Partners confirms that this application process is in compliance with applicable
              data protection laws. Our full privacy policy is available
              <a
                href={privacyPolicy()}
                className='text-primary-dark ms-2'
                style={{ fontWeight: 500, textDecoration: 'underline' }}
                target='_blank'
                rel='noopener noreferrer'
              >
                [Click Here]
              </a>
            </span>
          </div>
        </div>
        <div className='text-danger border-text'>
          {formik.errors.confirmCheckbox && <div>{formik.errors.confirmCheckbox}</div>}
        </div>

        <div className='cardNew stickeyFooter'>
          <div className='d-flex justify-content-between'>
            <button className='button button-outline-primary' onClick={backLink}>
              <LeftArowIcon color='currentColor' fontSize={12} />
              Back
            </button>
            <button
              type='submit'
              className='button button-primary'
              disabled={!fileGroupValidation() || formik.isSubmitting}
            >
              Save and Continue <RightAeroIcon color='currentColor' />
            </button>
          </div>
        </div>
      </form>

      {showAlert && (
        <Alert variant={alertType} showAlert={showAlert} setShowAlert={setShowAlert}>
          {alertMsg}
        </Alert>
      )}
    </div>
  )
}

export { Step3 }
